<template>
  <SfModal v-if="showModal" size="xs" @close="cancelConfirmation">
    <template #title>
      {{
        I18n.t('reconciliation_review.confirm_unreview_template_modal.title')
      }}
    </template>
    <template #body>
      <div
        v-dompurify-html="
          I18n.t(
            'reconciliation_review.confirm_unreview_template_modal.text1',
            { reviewers: reviewers }
          )
        "
        class="center"
      ></div>
    </template>

    <template #messages-bottom>
      <SfModalMessage variant="warning">
        {{
          I18n.t('reconciliation_review.confirm_unreview_template_modal.text2')
        }}
      </SfModalMessage>

      <div
        v-if="preparer"
        class="
          tw-flex
          tw-items-center
          tw-justify-center
          tw-gap-2
          tw-p-3
          tw-bg-gray-50
          tw-rounded
        "
      >
        <SfToggle
          v-model="autoUnprepare"
          @update:model-value="onToggleChange"
        />
        <label
          class="tw-text-sm tw-truncate tw-cursor-pointer tw-mb-0"
          @click="toggleAutoUnprepare"
        >
          {{
            I18n.t(
              'reconciliation_review.confirm_unreview_template_modal.toggle_label',
              { preparer: preparer }
            )
          }}
        </label>
      </div>
    </template>

    <template #footer>
      <div
        v-if="submitting"
        class="sf-spinner sf-spinner-primary sf-spinner-s sf-modal-xs"
      ></div>
      <div v-else class="tw-flex tw-gap-2">
        <SfButtonDs
          data-heap="remove-reviews-cancel"
          @click="cancelConfirmation"
        >
          {{ I18n.t('common.cancel') }}
        </SfButtonDs>
        <SfButtonDs
          variant="primary"
          data-heap="remove-reviews-confirm"
          @click="confirm"
        >
          {{ confirmButtonText }}
        </SfButtonDs>
      </div>
    </template>
  </SfModal>
</template>

<script lang="ts">
// Cache preference at module level so it survives component recreation
// (VueModalHandler creates a new Vue app on each modal open)
let cachedAutoUnprepare: boolean | undefined
</script>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount } from 'vue'

import { SfLog } from '@app/adapters/logger'
import { REFUSED } from '@app/shared/services/data-alteration-guard.constants'
import { SfModal, SfModalMessage } from '@app/shared/vue/components/sf-modal'
import { SfButtonDs } from '@silverfin/ui-components'
import SfToggle from '@app/shared/vue/components/sf-toggle.vue'
import { persistAutoUnprepareOnUnreview } from '@app/shared/api/user'
import useI18n from '@app/shared/vue/composables/use-I18n'

const I18n = useI18n()

const showModal = ref(true)
const reviewers = ref('')
const preparer = ref('')
const submitting = ref(false)
const autoUnprepare = ref(
  cachedAutoUnprepare ??
    window.Silverfin.currentUserPreferences?.auto_unprepare_on_unreview ??
    true
)

let requestId = ''
let awaitingAnswer = false
let keepOpenAfterConfirmation = false
let pendingPreferenceSave: Promise<void> | null = null
let previouslyHiddenModal: Element | null = null
let previouslyHiddenModalPortalModalIdentifier: ReturnType<
  typeof window.modalCitizen.closeModalPortal
> = null

const confirmButtonText = computed(() => {
  if (preparer.value && autoUnprepare.value) {
    return I18n.t(
      'reconciliation_review.confirm_unreview_template_modal.confirm_button_with_unprepare'
    )
  }
  return I18n.t(
    'reconciliation_review.confirm_unreview_template_modal.confirm_button'
  )
})

function openModal({
  requestId: guardRequestId,
  reviewers: reviewersList,
  preparer: preparerName,
  keepOpenAfterConfirmation: keepOpen,
}: {
  requestId: string
  reviewers: string
  preparer?: string
  keepOpenAfterConfirmation?: boolean
}) {
  showModal.value = true
  requestId = guardRequestId
  awaitingAnswer = true
  reviewers.value = reviewersList
  preparer.value = preparerName || ''
  keepOpenAfterConfirmation = keepOpen || false
  hideOtherOpenModal()
}

function answerGuard(verdict: { confirmed: boolean; skipUnprepare: boolean }) {
  awaitingAnswer = false
  window.eventHub.$emit('data-alteration-guard-result', {
    requestId,
    guard: 'unreview-confirmation',
    ...verdict,
  })
}

// The caller waits with no deadline, so a teardown that unmounts this component
// has to answer, or the save hangs with no way to retry.
onBeforeUnmount(() => {
  if (awaitingAnswer) answerGuard(REFUSED)
})

function cancelConfirmation() {
  answerGuard(REFUSED)
  window.eventHub.$emit('unreview-confirmation-cancelled')
  closeModal()
}

function closeModal() {
  showModal.value = false
}

async function confirm() {
  // The press settles the verdict, so read it and stop waiting here. A teardown
  // inside the await below would otherwise answer REFUSED first, and this
  // answer would reach a caller that has stopped listening.
  const verdict = {
    confirmed: true,
    skipUnprepare: preparer.value ? !autoUnprepare.value : false,
  }
  awaitingAnswer = false

  if (pendingPreferenceSave) {
    await pendingPreferenceSave
  }

  answerGuard(verdict)

  if (keepOpenAfterConfirmation === true) {
    submitting.value = true
  } else {
    closeModal()
    // We show it again because the attaching files to templates modal can
    // render errors
    showPreviouslyHiddenModal()
  }
}

function toggleAutoUnprepare() {
  autoUnprepare.value = !autoUnprepare.value
  onToggleChange(autoUnprepare.value)
}

async function onToggleChange(value: boolean) {
  cachedAutoUnprepare = value
  const savePromise = persistAutoUnprepareOnUnreview(value)
  pendingPreferenceSave = savePromise
  try {
    await savePromise
  } catch (e) {
    SfLog.warn('Failed to persist auto unprepare preference', {
      extra: { error: e },
    })
  } finally {
    if (pendingPreferenceSave === savePromise) {
      pendingPreferenceSave = null
    }
  }
}

function hideOtherOpenModal() {
  previouslyHiddenModal =
    document.querySelector('body > .sf-modal') || // Attaching files to templates
    document.querySelector(".sf-modal[data-show='true']") // Account setting edit modal

  if (previouslyHiddenModal) {
    // This is a workaround to hide open modals outside of the modal portal
    // E.g.:
    // - Attaching files to templates
    previouslyHiddenModal.classList.add('hidden')
  } else {
    previouslyHiddenModalPortalModalIdentifier =
      window.modalCitizen.closeModalPortal()
  }
}

// We open the previously hidden modal after this modal is closed because
// it can render errors after it finish its action.
function showPreviouslyHiddenModal() {
  if (previouslyHiddenModal) {
    previouslyHiddenModal.classList.remove('hidden')
  } else if (previouslyHiddenModalPortalModalIdentifier) {
    window.modalCitizen.openModalPortal(
      previouslyHiddenModalPortalModalIdentifier
    )
  }
}

defineExpose({
  openModal,
})
</script>
